import dayjs, { Dayjs } from 'dayjs';

interface ExpenseItem {
  id: string;
  description: string;
  category: 'utilities' | 'maintenance' | 'security' | 'administrative' | 'other';
  amount: number;
  date: Dayjs;
}

interface ExpenseReport {
  id: string;
  month: number; // 1-12
  year: number;
  title: string;
  summary?: string;
  totalAmount: number;
  fileUrl?: string;
  fileName?: string;
  items: ExpenseItem[];
  publishedDate: Dayjs;
}

// Helper function to get dates relative to current date
function getRelativeDate(daysOffset: number): Dayjs {
  return dayjs().add(daysOffset, 'day');
}

function getRelativeMonth(monthsOffset: number): { month: number; year: number } {
  const date = dayjs().add(monthsOffset, 'month');
  return { month: date.month() + 1, year: date.year() };
}

export const expenseReports: ExpenseReport[] = [
  // Current month
  {
    id: 'EXP001',
    ...getRelativeMonth(0),
    title: `Gastos Mensuales - ${dayjs().format('MMMM YYYY')}`,
    summary: 'Desglose completo de todos los gastos comunitarios del mes actual, incluyendo servicios públicos, mantenimiento, seguridad y costos administrativos.',
    totalAmount: 15875.50,
    fileUrl: '/reports/expenses-current-month.pdf',
    fileName: 'expenses-current-month.pdf',
    items: [
      { id: 'i1', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 4200.00, date: getRelativeDate(-25) },
      { id: 'i2', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 2100.00, date: getRelativeDate(-24) },
      { id: 'i3', description: 'Factura de Gas - Sistema de Calefacción', category: 'utilities', amount: 1800.00, date: getRelativeDate(-23) },
      { id: 'i4', description: 'Contrato de Mantenimiento de Ascensor', category: 'maintenance', amount: 3500.00, date: getRelativeDate(-20) },
      { id: 'i5', description: 'Salarios de Guardias de Seguridad', category: 'security', amount: 2800.00, date: getRelativeDate(-15) },
      { id: 'i6', description: 'Servicios de Limpieza', category: 'maintenance', amount: 1200.00, date: getRelativeDate(-10) },
      { id: 'i7', description: 'Suministros de Oficina', category: 'administrative', amount: 275.50, date: getRelativeDate(-5) },
    ],
    publishedDate: getRelativeDate(-1),
  },

  // Last month
  {
    id: 'EXP002',
    ...getRelativeMonth(-1),
    title: `Gastos Mensuales - ${dayjs().subtract(1, 'month').format('MMMM YYYY')}`,
    summary: 'Reporte detallado de gastos del mes anterior cubriendo todos los costos operativos.',
    totalAmount: 14250.75,
    fileUrl: '/reports/expenses-last-month.pdf',
    fileName: 'expenses-last-month.pdf',
    items: [
      { id: 'i8', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 3800.00, date: getRelativeDate(-55) },
      { id: 'i9', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 1950.00, date: getRelativeDate(-54) },
      { id: 'i10', description: 'Mantenimiento de Piscina', category: 'maintenance', amount: 2500.00, date: getRelativeDate(-50) },
      { id: 'i11', description: 'Actualización del Sistema de Seguridad', category: 'security', amount: 4000.00, date: getRelativeDate(-45) },
      { id: 'i12', description: 'Servicios de Jardinería', category: 'maintenance', amount: 1200.00, date: getRelativeDate(-40) },
      { id: 'i13', description: 'Prima de Seguro', category: 'administrative', amount: 800.75, date: getRelativeDate(-35) },
    ],
    publishedDate: getRelativeDate(-30),
  },

  // Two months ago
  {
    id: 'EXP003',
    ...getRelativeMonth(-2),
    title: `Gastos Mensuales - ${dayjs().subtract(2, 'month').format('MMMM YYYY')}`,
    summary: 'Desglose de gastos mensuales incluyendo mantenimiento estacional y costos de servicios públicos.',
    totalAmount: 13500.00,
    items: [
      { id: 'i14', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 3600.00, date: getRelativeDate(-85) },
      { id: 'i15', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 1800.00, date: getRelativeDate(-84) },
      { id: 'i16', description: 'Mantenimiento del Sistema HVAC', category: 'maintenance', amount: 3000.00, date: getRelativeDate(-80) },
      { id: 'i17', description: 'Salarios de Guardias de Seguridad', category: 'security', amount: 2800.00, date: getRelativeDate(-75) },
      { id: 'i18', description: 'Inspección de Seguridad contra Incendios', category: 'maintenance', amount: 1500.00, date: getRelativeDate(-70) },
      { id: 'i19', description: 'Consulta Legal', category: 'administrative', amount: 800.00, date: getRelativeDate(-65) },
    ],
    publishedDate: getRelativeDate(-60),
  },

  // Three months ago
  {
    id: 'EXP004',
    ...getRelativeMonth(-3),
    title: `Gastos Mensuales - ${dayjs().subtract(3, 'month').format('MMMM YYYY')}`,
    summary: 'Reporte de gastos trimestral con proyectos de mantenimiento importantes y costos operativos regulares.',
    totalAmount: 18200.25,
    fileUrl: '/reports/expenses-q3.pdf',
    fileName: 'expenses-q3.pdf',
    items: [
      { id: 'i20', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 4100.00, date: getRelativeDate(-115) },
      { id: 'i21', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 2200.00, date: getRelativeDate(-114) },
      { id: 'i22', description: 'Reparaciones del Techo', category: 'maintenance', amount: 8000.00, date: getRelativeDate(-110) },
      { id: 'i23', description: 'Instalación de Cámaras de Seguridad', category: 'security', amount: 2500.00, date: getRelativeDate(-105) },
      { id: 'i24', description: 'Renovación del Jardín', category: 'maintenance', amount: 1800.25, date: getRelativeDate(-100) },
      { id: 'i25', description: 'Servicios de Contabilidad', category: 'administrative', amount: 600.00, date: getRelativeDate(-95) },
    ],
    publishedDate: getRelativeDate(-90),
  },

  // Four months ago
  {
    id: 'EXP005',
    ...getRelativeMonth(-4),
    title: `Gastos Mensuales - ${dayjs().subtract(4, 'month').format('MMMM YYYY')}`,
    summary: 'Gastos operativos mensuales con enfoque en gestión de servicios públicos y seguridad.',
    totalAmount: 13950.00,
    items: [
      { id: 'i26', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 3700.00, date: getRelativeDate(-145) },
      { id: 'i27', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 1900.00, date: getRelativeDate(-144) },
      { id: 'i28', description: 'Servicio Anual de Ascensor', category: 'maintenance', amount: 4500.00, date: getRelativeDate(-140) },
      { id: 'i29', description: 'Salarios de Guardias de Seguridad', category: 'security', amount: 2800.00, date: getRelativeDate(-135) },
      { id: 'i30', description: 'Servicios de Control de Plagas', category: 'maintenance', amount: 800.00, date: getRelativeDate(-130) },
      { id: 'i31', description: 'Servicios de Internet', category: 'utilities', amount: 250.00, date: getRelativeDate(-125) },
    ],
    publishedDate: getRelativeDate(-120),
  },

  // Five months ago
  {
    id: 'EXP006',
    ...getRelativeMonth(-5),
    title: `Gastos Mensuales - ${dayjs().subtract(5, 'month').format('MMMM YYYY')}`,
    summary: 'Costos de mantenimiento de verano y servicios públicos incluyendo operaciones de piscina.',
    totalAmount: 15100.50,
    fileUrl: '/reports/expenses-summer.pdf',
    fileName: 'expenses-summer.pdf',
    items: [
      { id: 'i32', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 4300.00, date: getRelativeDate(-175) },
      { id: 'i33', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 2400.00, date: getRelativeDate(-174) },
      { id: 'i34', description: 'Mantenimiento de Piscina y Químicos', category: 'maintenance', amount: 2800.00, date: getRelativeDate(-170) },
      { id: 'i35', description: 'Mantenimiento de Aire Acondicionado', category: 'maintenance', amount: 2200.00, date: getRelativeDate(-165) },
      { id: 'i36', description: 'Salarios de Guardias de Seguridad', category: 'security', amount: 2800.00, date: getRelativeDate(-160) },
      { id: 'i37', description: 'Gastos de Eventos Comunitarios', category: 'other', amount: 600.50, date: getRelativeDate(-155) },
    ],
    publishedDate: getRelativeDate(-150),
  },

  // Six months ago
  {
    id: 'EXP007',
    ...getRelativeMonth(-6),
    title: `Gastos Mensuales - ${dayjs().subtract(6, 'month').format('MMMM YYYY')}`,
    summary: 'Reporte de gastos de mitad de año con proyectos de mantenimiento importantes.',
    totalAmount: 16800.00,
    items: [
      { id: 'i38', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 3900.00, date: getRelativeDate(-205) },
      { id: 'i39', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 2000.00, date: getRelativeDate(-204) },
      { id: 'i40', description: 'Reasfaltado del Estacionamiento', category: 'maintenance', amount: 7500.00, date: getRelativeDate(-200) },
      { id: 'i41', description: 'Mantenimiento del Sistema de Seguridad', category: 'security', amount: 1800.00, date: getRelativeDate(-195) },
      { id: 'i42', description: 'Inspección de Extintores', category: 'maintenance', amount: 1200.00, date: getRelativeDate(-190) },
      { id: 'i43', description: 'Tarifa de Gestión de Propiedades', category: 'administrative', amount: 2400.00, date: getRelativeDate(-185) },
    ],
    publishedDate: getRelativeDate(-180),
  },

  // Seven months ago
  {
    id: 'EXP008',
    ...getRelativeMonth(-7),
    title: `Gastos Mensuales - ${dayjs().subtract(7, 'month').format('MMMM YYYY')}`,
    summary: 'Mantenimiento de primavera y costos operativos regulares.',
    totalAmount: 14450.75,
    items: [
      { id: 'i44', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 3800.00, date: getRelativeDate(-235) },
      { id: 'i45', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 1950.00, date: getRelativeDate(-234) },
      { id: 'i46', description: 'Jardinería de Primavera', category: 'maintenance', amount: 3200.00, date: getRelativeDate(-230) },
      { id: 'i47', description: 'Salarios de Guardias de Seguridad', category: 'security', amount: 2800.00, date: getRelativeDate(-225) },
      { id: 'i48', description: 'Limpieza de Ventanas', category: 'maintenance', amount: 1500.00, date: getRelativeDate(-220) },
      { id: 'i49', description: 'Renovación de Seguro', category: 'administrative', amount: 1200.75, date: getRelativeDate(-215) },
    ],
    publishedDate: getRelativeDate(-210),
  },

  // Eight months ago
  {
    id: 'EXP009',
    ...getRelativeMonth(-8),
    title: `Gastos Mensuales - ${dayjs().subtract(8, 'month').format('MMMM YYYY')}`,
    summary: 'Mantenimiento de invierno y costos de calefacción.',
    totalAmount: 16200.00,
    fileUrl: '/reports/expenses-winter.pdf',
    fileName: 'expenses-winter.pdf',
    items: [
      { id: 'i50', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 4200.00, date: getRelativeDate(-265) },
      { id: 'i51', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 1800.00, date: getRelativeDate(-264) },
      { id: 'i52', description: 'Mantenimiento del Sistema de Calefacción', category: 'maintenance', amount: 3500.00, date: getRelativeDate(-260) },
      { id: 'i53', description: 'Servicios de Remoción de Nieve', category: 'maintenance', amount: 2800.00, date: getRelativeDate(-255) },
      { id: 'i54', description: 'Salarios de Guardias de Seguridad', category: 'security', amount: 2800.00, date: getRelativeDate(-250) },
      { id: 'i55', description: 'Combustible del Generador de Emergencia', category: 'utilities', amount: 1100.00, date: getRelativeDate(-245) },
    ],
    publishedDate: getRelativeDate(-240),
  },

  // Nine months ago
  {
    id: 'EXP010',
    ...getRelativeMonth(-9),
    title: `Gastos Mensuales - ${dayjs().subtract(9, 'month').format('MMMM YYYY')}`,
    summary: 'Mantenimiento de fin de año y costos administrativos.',
    totalAmount: 17550.25,
    items: [
      { id: 'i56', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 4100.00, date: getRelativeDate(-295) },
      { id: 'i57', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 2000.00, date: getRelativeDate(-294) },
      { id: 'i58', description: 'Inspección Anual del Edificio', category: 'maintenance', amount: 4500.00, date: getRelativeDate(-290) },
      { id: 'i59', description: 'Servicio Anual del Sistema de Seguridad', category: 'security', amount: 2200.00, date: getRelativeDate(-285) },
      { id: 'i60', description: 'Limpieza de Fin de Año', category: 'maintenance', amount: 1800.00, date: getRelativeDate(-280) },
      { id: 'i61', description: 'Servicios de Auditoría', category: 'administrative', amount: 2950.25, date: getRelativeDate(-275) },
    ],
    publishedDate: getRelativeDate(-270),
  },

  // Ten months ago
  {
    id: 'EXP011',
    ...getRelativeMonth(-10),
    title: `Gastos Mensuales - ${dayjs().subtract(10, 'month').format('MMMM YYYY')}`,
    summary: 'Mantenimiento de otoño y preparación para el invierno.',
    totalAmount: 14800.00,
    items: [
      { id: 'i62', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 3900.00, date: getRelativeDate(-335) },
      { id: 'i63', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 1900.00, date: getRelativeDate(-334) },
      { id: 'i64', description: 'Limpieza de Canalones', category: 'maintenance', amount: 1200.00, date: getRelativeDate(-330) },
      { id: 'i65', description: 'Salarios de Guardias de Seguridad', category: 'security', amount: 2800.00, date: getRelativeDate(-325) },
      { id: 'i66', description: 'Jardinería de Otoño', category: 'maintenance', amount: 2500.00, date: getRelativeDate(-320) },
      { id: 'i67', description: 'Preparación del Sistema de Calefacción', category: 'maintenance', amount: 2500.00, date: getRelativeDate(-315) },
    ],
    publishedDate: getRelativeDate(-300),
  },

  // Eleven months ago
  {
    id: 'EXP012',
    ...getRelativeMonth(-11),
    title: `Gastos Mensuales - ${dayjs().subtract(11, 'month').format('MMMM YYYY')}`,
    summary: 'Mantenimiento de final de verano y costos de servicios públicos.',
    totalAmount: 15350.75,
    fileUrl: '/reports/expenses-late-summer.pdf',
    fileName: 'expenses-late-summer.pdf',
    items: [
      { id: 'i68', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 4200.00, date: getRelativeDate(-365) },
      { id: 'i69', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 2300.00, date: getRelativeDate(-364) },
      { id: 'i70', description: 'Servicios de Cierre de Piscina', category: 'maintenance', amount: 1800.00, date: getRelativeDate(-360) },
      { id: 'i71', description: 'Salarios de Guardias de Seguridad', category: 'security', amount: 2800.00, date: getRelativeDate(-355) },
      { id: 'i72', description: 'Retoques de Pintura Exterior', category: 'maintenance', amount: 3200.75, date: getRelativeDate(-350) },
      { id: 'i73', description: 'Boletín Comunitario', category: 'administrative', amount: 50.00, date: getRelativeDate(-345) },
    ],
    publishedDate: getRelativeDate(-330),
  },

  // Twelve months ago
  {
    id: 'EXP013',
    ...getRelativeMonth(-12),
    title: `Gastos Mensuales - ${dayjs().subtract(12, 'month').format('MMMM YYYY')}`,
    summary: 'Mantenimiento anual y proyectos de reparación importantes.',
    totalAmount: 19800.00,
    items: [
      { id: 'i74', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 4000.00, date: getRelativeDate(-395) },
      { id: 'i75', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 2000.00, date: getRelativeDate(-394) },
      { id: 'i76', description: 'Reparaciones Importantes de Plomería', category: 'maintenance', amount: 8500.00, date: getRelativeDate(-390) },
      { id: 'i77', description: 'Reemplazo de Cámaras de Seguridad', category: 'security', amount: 3500.00, date: getRelativeDate(-385) },
      { id: 'i78', description: 'Control Anual de Plagas', category: 'maintenance', amount: 1200.00, date: getRelativeDate(-380) },
      { id: 'i79', description: 'Servicios Legales', category: 'administrative', amount: 600.00, date: getRelativeDate(-375) },
    ],
    publishedDate: getRelativeDate(-360),
  },

  // Thirteen months ago
  {
    id: 'EXP014',
    ...getRelativeMonth(-13),
    title: `Gastos Mensuales - ${dayjs().subtract(13, 'month').format('MMMM YYYY')}`,
    summary: 'Costos pico de servicios públicos de verano y mantenimiento.',
    totalAmount: 16950.50,
    items: [
      { id: 'i80', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 4800.00, date: getRelativeDate(-425) },
      { id: 'i81', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 2500.00, date: getRelativeDate(-424) },
      { id: 'i82', description: 'Servicios de Apertura de Piscina', category: 'maintenance', amount: 2000.00, date: getRelativeDate(-420) },
      { id: 'i83', description: 'Salarios de Guardias de Seguridad', category: 'security', amount: 2800.00, date: getRelativeDate(-415) },
      { id: 'i84', description: 'Reparaciones de Aire Acondicionado', category: 'maintenance', amount: 3800.00, date: getRelativeDate(-410) },
      { id: 'i85', description: 'Gastos de Eventos de Verano', category: 'other', amount: 1050.50, date: getRelativeDate(-405) },
    ],
    publishedDate: getRelativeDate(-390),
  },

  // Fourteen months ago
  {
    id: 'EXP015',
    ...getRelativeMonth(-14),
    title: `Gastos Mensuales - ${dayjs().subtract(14, 'month').format('MMMM YYYY')}`,
    summary: 'Mantenimiento de primavera y preparación para el verano.',
    totalAmount: 15600.00,
    fileUrl: '/reports/expenses-spring.pdf',
    fileName: 'expenses-spring.pdf',
    items: [
      { id: 'i86', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 4100.00, date: getRelativeDate(-455) },
      { id: 'i87', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 2100.00, date: getRelativeDate(-454) },
      { id: 'i88', description: 'Jardinería de Primavera', category: 'maintenance', amount: 3500.00, date: getRelativeDate(-450) },
      { id: 'i89', description: 'Salarios de Guardias de Seguridad', category: 'security', amount: 2800.00, date: getRelativeDate(-445) },
      { id: 'i90', description: 'Reparaciones de Iluminación Exterior', category: 'maintenance', amount: 1800.00, date: getRelativeDate(-440) },
      { id: 'i91', description: 'Revisión de Seguros', category: 'administrative', amount: 1300.00, date: getRelativeDate(-435) },
    ],
    publishedDate: getRelativeDate(-420),
  },

  // Fifteen months ago
  {
    id: 'EXP016',
    ...getRelativeMonth(-15),
    title: `Gastos Mensuales - ${dayjs().subtract(15, 'month').format('MMMM YYYY')}`,
    summary: 'Mantenimiento de invierno y costos de calefacción.',
    totalAmount: 17400.25,
    items: [
      { id: 'i92', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 4300.00, date: getRelativeDate(-485) },
      { id: 'i93', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 1900.00, date: getRelativeDate(-484) },
      { id: 'i94', description: 'Reparaciones del Sistema de Calefacción', category: 'maintenance', amount: 4200.00, date: getRelativeDate(-480) },
      { id: 'i95', description: 'Servicios de Remoción de Nieve', category: 'maintenance', amount: 3200.00, date: getRelativeDate(-475) },
      { id: 'i96', description: 'Salarios de Guardias de Seguridad', category: 'security', amount: 2800.00, date: getRelativeDate(-470) },
      { id: 'i97', description: 'Reparaciones de Emergencia', category: 'maintenance', amount: 1000.25, date: getRelativeDate(-465) },
    ],
    publishedDate: getRelativeDate(-450),
  },

  // Sixteen months ago
  {
    id: 'EXP017',
    ...getRelativeMonth(-16),
    title: `Gastos Mensuales - ${dayjs().subtract(16, 'month').format('MMMM YYYY')}`,
    summary: 'Gastos de fin de año y mantenimiento anual.',
    totalAmount: 18900.00,
    items: [
      { id: 'i98', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 4200.00, date: getRelativeDate(-515) },
      { id: 'i99', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 2000.00, date: getRelativeDate(-514) },
      { id: 'i100', description: 'Mantenimiento Anual del Edificio', category: 'maintenance', amount: 8500.00, date: getRelativeDate(-510) },
      { id: 'i101', description: 'Servicio Anual del Sistema de Seguridad', category: 'security', amount: 2500.00, date: getRelativeDate(-505) },
      { id: 'i102', description: 'Limpieza de Fin de Año', category: 'maintenance', amount: 2000.00, date: getRelativeDate(-500) },
      { id: 'i103', description: 'Auditoría Anual', category: 'administrative', amount: 2700.00, date: getRelativeDate(-495) },
    ],
    publishedDate: getRelativeDate(-480),
  },

  // Seventeen months ago
  {
    id: 'EXP018',
    ...getRelativeMonth(-17),
    title: `Gastos Mensuales - ${dayjs().subtract(17, 'month').format('MMMM YYYY')}`,
    summary: 'Mantenimiento de otoño y preparación para el invierno.',
    totalAmount: 15200.75,
    items: [
      { id: 'i104', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 4000.00, date: getRelativeDate(-545) },
      { id: 'i105', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 1950.00, date: getRelativeDate(-544) },
      { id: 'i106', description: 'Mantenimiento de Canalones', category: 'maintenance', amount: 1500.00, date: getRelativeDate(-540) },
      { id: 'i107', description: 'Salarios de Guardias de Seguridad', category: 'security', amount: 2800.00, date: getRelativeDate(-535) },
      { id: 'i108', description: 'Jardinería de Otoño', category: 'maintenance', amount: 2800.00, date: getRelativeDate(-530) },
      { id: 'i109', description: 'Preparación del Sistema de Calefacción', category: 'maintenance', amount: 2150.75, date: getRelativeDate(-525) },
    ],
    publishedDate: getRelativeDate(-510),
  },

  // Eighteen months ago
  {
    id: 'EXP019',
    ...getRelativeMonth(-18),
    title: `Gastos Mensuales - ${dayjs().subtract(18, 'month').format('MMMM YYYY')}`,
    summary: 'Mantenimiento de final de verano y costos de servicios públicos.',
    totalAmount: 16100.00,
    fileUrl: '/reports/expenses-late-summer-2.pdf',
    fileName: 'expenses-late-summer-2.pdf',
    items: [
      { id: 'i110', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 4500.00, date: getRelativeDate(-575) },
      { id: 'i111', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 2400.00, date: getRelativeDate(-574) },
      { id: 'i112', description: 'Mantenimiento de Piscina', category: 'maintenance', amount: 2800.00, date: getRelativeDate(-570) },
      { id: 'i113', description: 'Salarios de Guardias de Seguridad', category: 'security', amount: 2800.00, date: getRelativeDate(-565) },
      { id: 'i114', description: 'Reparaciones Exteriores', category: 'maintenance', amount: 2800.00, date: getRelativeDate(-560) },
      { id: 'i115', description: 'Evento Comunitario', category: 'other', amount: 800.00, date: getRelativeDate(-555) },
    ],
    publishedDate: getRelativeDate(-540),
  },

  // Nineteen months ago
  {
    id: 'EXP020',
    ...getRelativeMonth(-19),
    title: `Gastos Mensuales - ${dayjs().subtract(19, 'month').format('MMMM YYYY')}`,
    summary: 'Costos pico de servicios públicos de mitad de verano y mantenimiento.',
    totalAmount: 17800.50,
    items: [
      { id: 'i116', description: 'Factura de Electricidad - Áreas Comunes', category: 'utilities', amount: 5200.00, date: getRelativeDate(-605) },
      { id: 'i117', description: 'Factura de Agua - Áreas Comunes', category: 'utilities', amount: 2600.00, date: getRelativeDate(-604) },
      { id: 'i118', description: 'Mantenimiento de Aire Acondicionado', category: 'maintenance', amount: 3500.00, date: getRelativeDate(-600) },
      { id: 'i119', description: 'Salarios de Guardias de Seguridad', category: 'security', amount: 2800.00, date: getRelativeDate(-595) },
      { id: 'i120', description: 'Reparaciones de Equipos de Piscina', category: 'maintenance', amount: 2200.00, date: getRelativeDate(-590) },
      { id: 'i121', description: 'Gastos de Eventos de Verano', category: 'other', amount: 1500.50, date: getRelativeDate(-585) },
    ],
    publishedDate: getRelativeDate(-570),
  },
]; 